import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, ShieldAlert, Search, Copy, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { getIsAdmin } from "@/lib/reports.functions";
import { listGenerationPrompts } from "@/lib/admin-prompts.functions";

type Filter = "all" | "freeform_image" | "freeform_text" | "freeform_outfit" | "freeform_age";

export const Route = createFileRoute("/_authenticated/admin_/prompts")({
  head: () => ({
    meta: [
      { title: "プロンプト履歴 | 管理" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PromptsPage,
});

type Row = {
  id: string;
  user_id: string;
  category_id: string | null;
  category_params: Record<string, unknown> | null;
  prompt_used: string | null;
  prompt_original: string | null;
  prompt_translated: string | null;
  output_image_url: string | null;
  output_video_url: string | null;
  status: string;
  created_at: string;
};

function PromptsPage() {
  const isAdminFn = useServerFn(getIsAdmin);
  const listFn = useServerFn(listGenerationPrompts);

  const [checked, setChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Row | null>(null);

  const auth = async () => {
    const { data } = await supabase.auth.getSession();
    return { Authorization: `Bearer ${data.session?.access_token ?? ""}` };
  };

  useEffect(() => {
    (async () => {
      try {
        const headers = await auth();
        const r = await isAdminFn({ headers });
        setIsAdmin(r.isAdmin);
      } finally {
        setChecked(true);
      }
    })();
  }, [isAdminFn]);

  const load = async (f: Filter, s: string) => {
    setLoading(true);
    try {
      const headers = await auth();
      const r = await listFn({
        data: { category: f, search: s || undefined, limit: 100 },
        headers,
      });
      setRows(r.rows as Row[]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "読み込み失敗");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) void load(filter, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, filter]);

  if (!checked) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="min-h-screen bg-background px-4 py-16">
        <Card className="mx-auto max-w-md p-8 text-center">
          <ShieldAlert className="mx-auto h-12 w-12 text-destructive" />
          <h1 className="mt-4 text-2xl font-bold">アクセス権がありません</h1>
          <Button asChild className="mt-6">
            <Link to="/dashboard">ダッシュボードへ</Link>
          </Button>
        </Card>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">プロンプト履歴</h1>
            <p className="text-sm text-muted-foreground">
              画像生成に使用されたプロンプトを確認できます
            </p>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to="/admin">
              <ArrowLeft className="mr-2 h-4 w-4" />
              管理画面へ
            </Link>
          </Button>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
            <TabsList>
              <TabsTrigger value="all">全て</TabsTrigger>
              <TabsTrigger value="freeform_image">画像</TabsTrigger>
              <TabsTrigger value="freeform_text">テキスト</TabsTrigger>
              <TabsTrigger value="freeform_outfit">衣装</TabsTrigger>
              <TabsTrigger value="freeform_age">年齢</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex flex-1 items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="プロンプト内検索"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void load(filter, search);
                }}
              />
            </div>
            <Button size="sm" onClick={() => void load(filter, search)} disabled={loading}>
              検索
            </Button>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {loading && (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}
          {!loading && rows.length === 0 && (
            <Card className="p-8 text-center text-sm text-muted-foreground">
              該当する生成がありません
            </Card>
          )}
          {!loading &&
            rows.map((r) => (
              <Card
                key={r.id}
                className="cursor-pointer p-4 transition hover:bg-muted/40"
                onClick={() => setSelected(r)}
              >
                <div className="flex items-start gap-4">
                  {r.output_image_url ? (
                    <img
                      src={r.output_image_url}
                      alt=""
                      className="h-20 w-20 flex-shrink-0 rounded object-cover"
                    />
                  ) : (
                    <div className="h-20 w-20 flex-shrink-0 rounded bg-muted" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <Badge variant="outline">{r.category_id ?? "—"}</Badge>
                      <Badge variant={r.status === "completed" ? "default" : "secondary"}>
                        {r.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleString("ja-JP")}
                      </span>
                    </div>
                    <p className="line-clamp-2 text-sm text-foreground/90">
                      {r.prompt_used ?? "—"}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>プロンプト詳細</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">{selected.category_id ?? "—"}</Badge>
                <Badge>{selected.status}</Badge>
                <span className="text-xs text-muted-foreground">
                  {new Date(selected.created_at).toLocaleString("ja-JP")}
                </span>
              </div>
              {selected.output_image_url && (
                <img
                  src={selected.output_image_url}
                  alt=""
                  className="max-h-80 rounded border"
                />
              )}
              <PromptBlock label="使用プロンプト (Nano Banana Pro)" value={selected.prompt_used} />
              <PromptBlock label="ユーザー入力 (原文)" value={selected.prompt_original} />
              <PromptBlock label="翻訳結果" value={selected.prompt_translated} />
              <div>
                <div className="mb-1 text-xs font-medium text-muted-foreground">
                  category_params
                </div>
                <pre className="max-h-60 overflow-auto rounded bg-muted p-3 text-xs">
                  {JSON.stringify(selected.category_params ?? {}, null, 2)}
                </pre>
              </div>
              <div className="text-xs text-muted-foreground">
                user_id: <span className="font-mono">{selected.user_id}</span>
                <br />
                generation_id: <span className="font-mono">{selected.id}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}

function PromptBlock({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            void navigator.clipboard.writeText(value);
            toast.success("コピーしました");
          }}
        >
          <Copy className="mr-1 h-3 w-3" />
          コピー
        </Button>
      </div>
      <pre className="whitespace-pre-wrap rounded bg-muted p-3 text-xs">{value}</pre>
    </div>
  );
}
